import * as i0 from '@angular/core';
import { Injectable, Directive, Input, HostListener } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import * as i1 from '@angular/common/http';
import * as i4 from '@wdpr/ra-angular-page-key';

class ConfigService {
    http;
    loader = this.defaultLoader;
    autoPageViewTrackingTiming = 1000;
    globalProcessors = {};
    serverHost = '';
    serverPath = 'analytics';
    constructor(http) {
        this.http = http;
    }
    addGlobalProcessors(processors) {
        return Object.assign(this.globalProcessors, processors);
    }
    defaultLoader(pagekey) {
        return firstValueFrom(this.http.get(`${this.serverHost}/${this.serverPath}/${pagekey}.json`));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: ConfigService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: ConfigService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: ConfigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }] });

class GlobalService {
    config;
    properties = {
        customerId,
        registrationStatus,
        guestType,
        events: [loggedInOrOutEvent, registrationEvent],
        contentLocale,
        contentLanguage,
        device: {
            type: deviceType,
            viewType: deviceViewType,
        },
        configuration: {
            currency: configurationCurrency,
        },
        currencyDisplayed
    };
    constructor(config) {
        this.config = config;
    }
    augment(model) {
        return _meld(_normalizeModel(model), _merge(this.properties, _normalizeModel(this.config.globalProcessors)));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: GlobalService, deps: [{ token: ConfigService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: GlobalService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: GlobalService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: ConfigService }] });
function customerId() {
    return getCookie('SWID');
}
function registrationStatus() {
    const cid = getCookie('SWID'), isRegistered = typeof cid === 'string' && cid.startsWith('{'), isLoggedIn = getCookie('authenticationSecure');
    if (isLoggedIn) {
        return 'Registered - Logged In';
    }
    else if (isRegistered) {
        return 'Registered - Not Logged In';
    }
    return 'Not Registered';
}
function guestType() {
    return 'Guest';
}
function loggedInOrOutEvent() {
    const isLoggedIn = String(Boolean(getCookie('authenticationSecure'))), storage = verifyStorage(window.sessionStorage);
    let previousState, result;
    if (storage) {
        previousState = storage.getItem('analytics-logged-in-state');
        if (previousState === 'false' && isLoggedIn === 'true') {
            result = 'loggedIn';
        }
        else if (previousState === 'true' && isLoggedIn === 'false') {
            result = 'loggedOut';
        }
        storage.setItem('analytics-logged-in-state', isLoggedIn);
    }
    return result;
}
function registrationEvent() {
    // TODO: determine if detecting registration is possible due to registration occuring in PEP
    // if (guestJustRegistered) {
    //     return 'registration';
    // }
}
function contentLocale() {
    const cookieLocale = _getCookieLocale();
    if (cookieLocale) {
        const locale = cookieLocale.slice(3);
        if (locale !== 'XX') {
            return locale;
        }
    }
    return undefined;
}
function contentLanguage() {
    const cookieLocale = _getCookieLocale();
    return cookieLocale ? cookieLocale.slice(0, 2) : undefined;
}
function deviceType() {
    const WDPROView = getCookieObject('WDPROView');
    return WDPROView?.deviceInfo?.device || WDPROView?.preferred?.device || 'desktop';
}
function deviceViewType() {
    const WDPROView = getCookieObject('WDPROView');
    return WDPROView?.preferred?.device || 'desktop';
}
function configurationCurrency() {
    const localeCookie = getCookieObject('localeCookie_jar');
    return localeCookie?.localeCurrency;
}
function currencyDisplayed() {
    return configurationCurrency();
}
/* ------------------------------------------------------------------------------------------------------ */
function getCookie(name) {
    const cookies = document.cookie.split('; ');
    for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        if (cookie.startsWith(name + '=')) {
            return cookie.substring(name.length + 1);
        }
    }
    return '';
}
function getCookieObject(name) {
    try {
        return JSON.parse(getCookie(name));
    }
    catch (ignore) { }
    return {};
}
function verifyStorage(storage) {
    const k = 'test', v = 'yes';
    try {
        storage.setItem(k, v);
        if (storage.getItem(k) !== v) {
            return false;
        }
        storage.removeItem(k);
    }
    catch (e) {
        return false;
    }
    return storage;
}
function _getCookieLocale() {
    const localeCookie = getCookieObject('localeCookie_jar');
    let value;
    if (localeCookie) {
        value = localeCookie.preferredRegion || localeCookie.contentLocale;
        if (typeof value === 'string') {
            if (value.length === 5 && (value[2] === '_' || value[2] === '-')) {
                return value;
            }
            else if (value.length === 2) {
                return value + '_XX';
            }
        }
    }
    // Return a default value in case none of the conditions are met
    return null;
}
function _merge(target, source) {
    let key, k, keysLength, value, valueType;
    const keys = Object.keys(source);
    for (k = 0, keysLength = keys.length; k < keysLength; k++) {
        key = keys[k];
        value = source[key];
        valueType = _type(value);
        switch (valueType) {
            case 'object':
                if (_type(target[key]) !== 'object') {
                    target[key] = {};
                }
                _merge(target[key], value);
                break;
            case 'array':
                if (_type(target[key]) !== 'array') {
                    target[key] = [];
                }
                target[key].push.apply(target[key], value);
                break;
            default:
                target[key] = value;
        }
    }
    return target;
}
function _normalizeModel(model) {
    if (model && model.events && typeof model.events === 'string') {
        model.events = model.events.split(',');
    }
    return model;
}
function _type(o) {
    const type = typeof o;
    if (type === 'object') {
        if (o === null) {
            return 'null';
        }
        else if (Array.isArray(o)) {
            return 'array';
        }
    }
    return type;
}
function _meld(model, properties) {
    return _merge(model, m(properties) || {});
    function m(value) {
        switch (_type(value)) {
            case 'object': {
                const replacement = {};
                const keys = Object.keys(value);
                for (const key of keys) {
                    const val = m(value[key]);
                    if (val !== undefined) {
                        replacement[key] = val;
                    }
                }
                return Object.keys(replacement).length ? replacement : undefined;
            }
            case 'array': {
                const replacement = value
                    .map(m)
                    .filter((v) => v !== undefined);
                return replacement.length ? replacement : undefined;
            }
            case 'function':
                return m(value(model));
            default:
                return value;
        }
    }
}

class FrameworkService {
    reset() {
        // noop
    }
    update(analyticsObj) {
        const win = window;
        (win.DisneyDataLayer || (win.DisneyDataLayer = [])).push(['trackPage', analyticsObj]);
    }
    trackElement(analyticsObj) {
        const win = window;
        (win.DisneyDataLayer || (win.DisneyDataLayer = [])).push(['trackElement', analyticsObj]);
    }
    framework() {
        return this;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: FrameworkService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: FrameworkService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: FrameworkService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class AnalyticsService {
    config;
    global;
    framework;
    pageKeyService;
    pageKey = '';
    timerId = 0;
    modelPromise;
    constructor(config, global, framework, pageKeyService) {
        this.config = config;
        this.global = global;
        this.framework = framework;
        this.pageKeyService = pageKeyService;
        this.pageKeySubscribe();
    }
    update() {
        this.getModel().then((model) => {
            this.framework.update(model);
        });
    }
    getModel() {
        this.cancelAutoPageTracking();
        return (this.modelPromise ||
            (this.modelPromise = Promise.resolve(this.config.loader(this.pageKey))
                .then(validateModel)
                .then(this.global.augment.bind(this.global))));
    }
    trackElement(model) {
        if (model.linkId) {
            this.framework.trackElement(model);
        }
    }
    trackPage(model) {
        if (model.pageId) {
            this.trackElement(Object.assign(model, { trackingType: 'customPage' }));
        }
    }
    trackLink(model) {
        this.trackElement(Object.assign(model, { trackingType: 'customLink' }));
    }
    pageKeySubscribe() {
        this.pageKeyService.subscribe((pageKey) => this.scheduleAutoPageTracking(pageKey.key));
    }
    cancelAutoPageTracking() {
        clearTimeout(this.timerId);
    }
    scheduleAutoPageTracking(pageKey) {
        this.pageKey = pageKey;
        this.modelPromise = undefined;
        this.cancelAutoPageTracking();
        this.timerId = setTimeout(() => this.update(), this.config.autoPageViewTrackingTiming);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: AnalyticsService, deps: [{ token: ConfigService }, { token: GlobalService }, { token: FrameworkService }, { token: i4.PageKeyService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: AnalyticsService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: AnalyticsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: ConfigService }, { type: GlobalService }, { type: FrameworkService }, { type: i4.PageKeyService }] });
function validateModel(m) {
    return typeof m === 'object' && m !== null && !Array.isArray(m)
        ? m
        : Promise.reject('No valid semantic analytics model.');
}

function directiveModel(inputStr) {
    if (!inputStr)
        return {};
    let val = inputStr;
    try {
        val = decodeURIComponent(val);
    }
    catch (ignore) {
        // ignore
    }
    if (val.startsWith('{')) {
        try {
            val = JSON.parse(val);
        }
        catch (ignore) {
            // ignore
        }
    }
    if (typeof val === 'string') {
        val = {
            linkId: val
        };
    }
    return val;
}
;

class TrackHoverDirective {
    analytics;
    model = '';
    onMouseEnter() {
        this.analytics.trackLink(directiveModel(this.model));
    }
    constructor(analytics) {
        this.analytics = analytics;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: TrackHoverDirective, deps: [{ token: AnalyticsService }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.5", type: TrackHoverDirective, isStandalone: true, selector: "[analyticsTrackHover]", inputs: { model: ["analyticsTrackHover", "model"] }, host: { listeners: { "mouseenter": "onMouseEnter()" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: TrackHoverDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[analyticsTrackHover]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: AnalyticsService }], propDecorators: { model: [{
                type: Input,
                args: ['analyticsTrackHover']
            }], onMouseEnter: [{
                type: HostListener,
                args: ['mouseenter']
            }] } });

class TrackLinkDirective {
    analytics;
    model = '';
    onClick() {
        this.analytics.trackLink(directiveModel(this.model));
    }
    constructor(analytics) {
        this.analytics = analytics;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: TrackLinkDirective, deps: [{ token: AnalyticsService }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.5", type: TrackLinkDirective, isStandalone: true, selector: "[analyticsTrackLink]", inputs: { model: ["analyticsTrackLink", "model"] }, host: { listeners: { "click": "onClick()" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: TrackLinkDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[analyticsTrackLink]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: AnalyticsService }], propDecorators: { model: [{
                type: Input,
                args: ['analyticsTrackLink']
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

class TrackPageDirective {
    analytics;
    model = '';
    onClick() {
        this.analytics.trackPage(directiveModel(this.model));
    }
    constructor(analytics) {
        this.analytics = analytics;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: TrackPageDirective, deps: [{ token: AnalyticsService }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.5", type: TrackPageDirective, isStandalone: true, selector: "[analyticsTrackPage]", inputs: { model: ["analyticsTrackPage", "model"] }, host: { listeners: { "click": "onClick()" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: TrackPageDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[analyticsTrackPage]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: AnalyticsService }], propDecorators: { model: [{
                type: Input,
                args: ['analyticsTrackPage']
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

class TrackSeenDirective {
    analytics;
    _elementRef;
    _element;
    _trackingFired;
    _throttledChecker;
    model = '';
    onWindowChange() {
        this._throttledChecker();
    }
    ngAfterViewInit() {
        this._throttledChecker();
    }
    constructor(analytics, _elementRef) {
        this.analytics = analytics;
        this._elementRef = _elementRef;
        this._element = this._elementRef.nativeElement;
        this._trackingFired = false;
        this._throttledChecker = this._throttle(this._checkAndFireTrackElement, 250);
    }
    _checkAndFireTrackElement() {
        if (!this._trackingFired && this._isElementVisible(this._element)) {
            this._fireTracking(directiveModel(this.model));
        }
    }
    _isElementVisible(element) {
        let VISIBLE_MARGIN = 1, hasRects = !!element.getClientRects().length, result = hasRects, rect;
        if (hasRects) {
            rect = element.getBoundingClientRect();
            result = !(rect.top > window.innerHeight - VISIBLE_MARGIN ||
                rect.bottom < VISIBLE_MARGIN);
        }
        return result;
    }
    _fireTracking(model) {
        this.analytics.trackElement(model);
        this._trackingFired = true;
    }
    _throttle(originalFunction, throttleInterval) {
        let timerId = null;
        let callContext;
        let callArguments = null;
        let nextEligibleCallTime = Date.now();
        let cachedReturn;
        const executeOriginalFunction = (now) => {
            timerId = null;
            nextEligibleCallTime = (now || Date.now()) + throttleInterval;
            cachedReturn = originalFunction.apply(callContext, callArguments);
            return cachedReturn;
        };
        return () => {
            const now = Date.now();
            callContext = this;
            callArguments = arguments;
            if (timerId) {
                return cachedReturn;
            }
            if (now < nextEligibleCallTime) {
                timerId = setTimeout(() => executeOriginalFunction(now), nextEligibleCallTime - now);
                return cachedReturn;
            }
            else {
                return executeOriginalFunction(now);
            }
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: TrackSeenDirective, deps: [{ token: AnalyticsService }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.5", type: TrackSeenDirective, isStandalone: true, selector: "[analyticsTrackSeen]", inputs: { model: ["analyticsTrackSeen", "model"] }, host: { listeners: { "window:resize": "onWindowChange()", "window:scroll": "onWindowChange()" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.5", ngImport: i0, type: TrackSeenDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[analyticsTrackSeen]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: AnalyticsService }, { type: i0.ElementRef }], propDecorators: { model: [{
                type: Input,
                args: ['analyticsTrackSeen']
            }], onWindowChange: [{
                type: HostListener,
                args: ['window:resize']
            }, {
                type: HostListener,
                args: ['window:scroll']
            }] } });

/*
 * Public API Surface of wdpr-angular-analytics
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AnalyticsService, ConfigService, FrameworkService, GlobalService, TrackHoverDirective, TrackLinkDirective, TrackPageDirective, TrackSeenDirective };
